<template>
  <div class="ov-pay" :class="{ 'ov-pay--side': isSide }">
    <div class="ov-pay__donut">
      <svg :width="size" :height="size" :viewBox="viewBox" aria-hidden="true" focusable="false">
        <circle
          v-for="seg in segments"
          :key="seg.key"
          class="ov-pay__seg"
          :class="'ov-pay__seg--' + seg.kind"
          :cx="center"
          :cy="center"
          :r="radius"
          fill="none"
          :stroke-width="ring"
          :stroke-dasharray="seg.dasharray"
          :stroke-dashoffset="seg.dashoffset"
          :transform="rotate"
        ></circle>
      </svg>
    </div>
    <div class="ov-pay__rows">
      <p v-if="empty" class="ov-pay__empty">Keine Verkäufe im {{ monthLabel }}</p>
      <template v-else>
        <div v-for="row in rows" :key="row.key" class="ov-pay__row" :style="rowStyle">
          <span class="ov-pay__swatch" :class="'ov-pay__swatch--' + row.key" aria-hidden="true"></span>
          <span class="ov-pay__label">{{ row.label }}</span>
          <span class="ov-pay__amount">{{ row.amount }}</span>
          <span class="ov-pay__share">{{ row.share }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
// Zahlungsarten (Spezifikation 4.1.5, gen_r3.py:589–615): SVG-Donut ohne Spur und ohne Mitteltext, Bar --d-foc und
// Karte --d-cmp mit weißen 2-px-Fugen, negative Werte im Diagramm als 0; ohne Verkäufe leerer Ring --d-trk mit Hinweis.
// Zeilen (kein Link): Farbfeld, Bezeichnung, Betrag, Anteil. Gutschein (seit Oktober 2026, Drucker-App) als dritter
// Anteil --d-std, Zeile nur mit Gutscheinen im Monat. Layout: Donut oben mittig, Zeilen darunter; ab einer
// Kartenbreite von 480 px (ResizeObserver) Donut links und Zeilen rechts. side: immer nebeneinander (Handy, Donut 104).
import { formatEuro, formatNumber, LEER, NBSP } from '@/utils/format';

// 480 px Kartenbreite abzüglich Kartenkante (2 × 1) und Körperpolster (2 × 16)
const SIDE_MIN_WIDTH = 480 - 2 - 32;

function share(part, total) {
  return total > 0 ? formatNumber(part / total * 100, 0) + NBSP + '%' : LEER;
}

export default {
  name: 'OvPaymentSplit',
  props: {
    cash: { type: Number, default: 0 },
    card: { type: Number, default: 0 },
    voucher: { type: Number, default: 0 },
    // „Oktober 2026“ für den Leerzustand „Keine Verkäufe im Oktober 2026“
    monthLabel: { type: String, default: '' },
    // Donut 140/12 (Tablet hoch 120/12, Handy 104/10)
    size: { type: Number, default: 140 },
    ring: { type: Number, default: 12 },
    // Zeilenhöhe 44 (Tablet hoch 48)
    rowHeight: { type: Number, default: 44 },
    side: { type: Boolean, default: false }
  },
  data() {
    return { wide: false };
  },
  computed: {
    isSide() {
      return this.side || this.wide;
    },
    empty() {
      return this.cash === 0 && this.card === 0 && this.voucher === 0;
    },
    // Anteile in Reihenfolge Bar, Karte, Gutschein (Gutschein nur, wenn vorhanden)
    parts() {
      const parts = [
        { key: 'cash', label: 'Bar', value: this.cash },
        { key: 'card', label: 'Karte', value: this.card }
      ];
      if (this.voucher !== 0) {
        parts.push({ key: 'voucher', label: 'Gutschein', value: this.voucher });
      }
      return parts;
    },
    center() {
      return this.size / 2;
    },
    radius() {
      return (this.size - this.ring) / 2;
    },
    viewBox() {
      return '0 0 ' + this.size + ' ' + this.size;
    },
    rotate() {
      return 'rotate(-90 ' + this.center + ' ' + this.center + ')';
    },
    // Bögen je positivem Anteil mit 2-px-Fugen; ein einziger Anteil als voller Ring
    segments() {
      const positive = this.parts.map((p) => ({ key: p.key, value: Math.max(0, p.value) })).filter((p) => p.value > 0);
      const total = positive.reduce((acc, p) => acc + p.value, 0);
      if (!(total > 0)) {
        return [{ key: 'empty', kind: 'empty', dasharray: null, dashoffset: null }];
      }
      if (positive.length === 1) {
        return [{ key: 'full', kind: positive[0].key, dasharray: null, dashoffset: null }];
      }
      const c = 2 * Math.PI * this.radius;
      let start = 0;
      return positive.map((p) => {
        const f = p.value / total;
        const seg = {
          key: p.key,
          kind: p.key,
          dasharray: Math.max(f * c - 2, 0.01).toFixed(2) + ' ' + c.toFixed(2),
          dashoffset: start ? (-start * c).toFixed(2) : null
        };
        start += f;
        return seg;
      });
    },
    rows() {
      const total = this.parts.reduce((acc, p) => acc + p.value, 0);
      return this.parts.map((p) => ({ key: p.key, label: p.label, amount: formatEuro(p.value), share: share(p.value, total) }));
    },
    rowStyle() {
      return { minHeight: this.rowHeight + 'px' };
    }
  },
  mounted() {
    this.measure();
    if (typeof window.ResizeObserver === 'function') {
      this.observer = new window.ResizeObserver(() => this.measure());
      this.observer.observe(this.$el);
    } else {
      window.addEventListener('resize', this.measure);
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    } else {
      window.removeEventListener('resize', this.measure);
    }
  },
  methods: {
    measure() {
      const width = this.$el ? this.$el.clientWidth : 0;
      if (width > 0) {
        this.wide = width >= SIDE_MIN_WIDTH;
      }
    }
  }
};
</script>

<style scoped>
.ov-pay {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
}
.ov-pay--side {
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
}
.ov-pay__donut {
  flex: none;
  display: flex;
  justify-content: center;
}
.ov-pay__donut svg {
  display: block;
}
.ov-pay__seg--cash {
  stroke: var(--d-foc);
}
.ov-pay__seg--card {
  stroke: var(--d-cmp);
}
.ov-pay__seg--voucher {
  stroke: var(--d-std);
}
.ov-pay__seg--empty {
  stroke: var(--d-trk);
}
.ov-pay__rows {
  min-width: 0;
}
.ov-pay--side .ov-pay__rows {
  flex: 1 1 auto;
}
.ov-pay__row {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  line-height: 20px;
}
.ov-pay__row + .ov-pay__row {
  border-top: 1px solid var(--line-2);
}
.ov-pay__swatch {
  width: 10px;
  height: 10px;
  flex: none;
  border-radius: 2px;
}
.ov-pay__swatch--cash {
  background: var(--d-foc);
}
.ov-pay__swatch--voucher {
  background: var(--d-std);
}
.ov-pay__swatch--card {
  background: var(--d-cmp);
}
.ov-pay__label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.ov-pay__amount {
  flex: none;
  font-weight: 600;
  white-space: nowrap;
}
.ov-pay__share {
  width: 34px;
  flex: none;
  text-align: right;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
  white-space: nowrap;
}
p.ov-pay__empty {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink-3);
  text-align: center;
}
.ov-pay--side p.ov-pay__empty {
  text-align: left;
}
</style>
